import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useAlert } from "react-alert";
import { useSelector } from "react-redux";
import { RiSendPlaneLine } from "react-icons/ri";
import {
  AttachFile,
  ChatBubbleOutline,
  Delete,
  ErrorOutline,
  InsertDriveFileOutlined,
  PersonOutline,
  Refresh,
  Search,
  Send,
} from "@material-ui/icons";
import { baseUrl, config } from "../../../services/config";
import "./teacher-chat.css";
import { BsChatSquareText } from "react-icons/bs";
import useAdaptiveChatPolling from "./useAdaptiveChatPolling";

const STAFF_CHAT_ROLES = [
  "teacher",
  "class teacher",
  "class incharge",
  "class_incharge",
];

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-PK", {
    day: "2-digit",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function fileUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${baseUrl.replace(/\/api\/?$/, "")}/storage/${path}`;
}

const studentFatherName = (student) => student?.father_name || "Unknown Name";

const studentAdmission = (student) =>
  student?.admission_id ? `Admission #${student.admission_id}` : "New chat";

const studentShortAdmission = (student) =>
  student?.admission_id ? `Adm #${student.admission_id}` : "New chat";

const studentClassName = (student) =>
  student?.student_class_name ||
  student?.student_class?.name ||
  student?.class_name ||
  "";

const studentSectionName = (student) =>
  student?.global_section_name ||
  student?.global_section?.name ||
  student?.section_name ||
  "";

const studentAcademicHeader = (student) =>
  [
    studentFatherName(student),
    studentAdmission(student),
    studentClassName(student),
    studentSectionName(student),
  ]
    .filter(Boolean)
    .join(" - ");

const studentAcademicLine = (student) =>
  [
    studentShortAdmission(student),
    studentClassName(student),
    studentSectionName(student),
  ]
    .filter(Boolean)
    .join(" - ");

const conversationStudent = (conversation) => ({
  ...(conversation?.student || {}),
  admission_id:
    conversation?.student?.admission_id || conversation?.admission_id,
  student_class_name:
    conversation?.student?.student_class_name ||
    conversation?.student_class_name ||
    conversation?.student_class?.name,
  global_section_name:
    conversation?.student?.global_section_name ||
    conversation?.global_section_name ||
    conversation?.global_section?.name,
});

const mergeConversationStudent = (conversation, fallbackConversation) => {
  if (!conversation) return conversation;
  return {
    ...conversation,
    student: {
      ...(fallbackConversation?.student || {}),
      ...(conversation.student || {}),
    },
  };
};

const hasClassAndSection = (student) =>
  Boolean(studentClassName(student) && studentSectionName(student));

const payloadStudent = (payload) => {
  if (Array.isArray(payload)) return payload[0] || null;
  return payload?.student || payload || null;
};

const normalizeRoleName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[-_]+/g, " ");

const hasStaffChatRole = (user) => {
  const roles = Array.isArray(user?.roles)
    ? user.roles
    : Array.isArray(user?.role)
    ? user.role
    : [];

  if (!roles.length) return Boolean(user?.id);

  return roles.some((role) =>
    STAFF_CHAT_ROLES.includes(normalizeRoleName(role?.name || role)),
  );
};

export default function TeacherChat() {
  const alert = useAlert();
  const user = useSelector((state) => state.user.user);
  const token = localStorage.getItem("userToken");
  const [conversations, setConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [history, setHistory] = useState(null);
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [conversationSearch, setConversationSearch] = useState("");
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [refreshingChat, setRefreshingChat] = useState(false);
  const [sending, setSending] = useState(false);
  const [studentModal, setStudentModal] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [campuses] = useState(user?.campuses || []);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentLoading, setStudentLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState("");
  const [selectedCampus, setSelectedCampus] = useState(
    user?.campuses?.[0]?.id || "",
  );
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedEducationType, setSelectedEducationType] = useState(1);
  const [studentSearch, setStudentSearch] = useState("");
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const studentDetailsRef = useRef(new Map());

  const teacherId = user?.id;
  const activeConversation = history?.conversation || activeChat?.conversation;
  const messages = history?.messages || [];
  const totalUnreadCount = useMemo(
    () =>
      conversations.reduce(
        (total, conversation) => total + Number(conversation.unread_count || 0),
        0,
      ),
    [conversations],
  );

  const visibleConversations = useMemo(() => {
    const query = conversationSearch.trim().toLowerCase();
    if (!query) return conversations;
    return conversations.filter((conversation) =>
      `${conversation.student?.name || ""} ${studentAcademicLine(
        conversationStudent(conversation),
      )}`
        .toLowerCase()
        .includes(query),
    );
  }, [conversationSearch, conversations]);

  const visibleStudents = useMemo(() => {
    const query = studentSearch.trim().toLowerCase();
    return students.filter((student) => {
      if (!query) return true;
      return `${student.name || ""} ${student.admission_id ||
        ""} ${student.father_name || ""} ${studentClassName(
        student,
      )} ${studentSectionName(student)}`
        .toLowerCase()
        .includes(query);
    });
  }, [studentSearch, students]);

  const loadStudentDetail = async (studentId) => {
    if (!studentId) return null;
    if (studentDetailsRef.current.has(Number(studentId))) {
      return studentDetailsRef.current.get(Number(studentId));
    }
    try {
      const response = await fetch(`${baseUrl}/student/${studentId}`, {
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const result = await response.json();
      if (!response.ok) return null;
      const detail = payloadStudent(result?.payload);
      if (detail) studentDetailsRef.current.set(Number(studentId), detail);
      return detail;
    } catch (error) {
      return null;
    }
  };

  const enrichConversations = async (conversationItems) =>
    Promise.all(
      conversationItems.map(async (conversation) => {
        const mergedStudent = conversationStudent(conversation);
        if (hasClassAndSection(mergedStudent)) {
          return mergeConversationStudent(conversation, {
            student: mergedStudent,
          });
        }
        const studentDetail = await loadStudentDetail(conversation.student_id);
        return studentDetail
          ? mergeConversationStudent(conversation, { student: studentDetail })
          : conversation;
      }),
    );

  const loadConversations = async (options = {}) => {
    if (!options.silent) setLoadingConversations(true);
    try {
      const response = await fetch(`${baseUrl}/conversations`, {
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(
          result?.metadata?.message || "Unable to load conversations.",
        );
      }
      const payload = Array.isArray(result?.payload) ? result.payload : [];
      const filtered = payload.filter(
        (conversation) =>
          !teacherId || Number(conversation.teacher_id) === Number(teacherId),
      );
      const enriched = await enrichConversations(filtered);
      setConversations(enriched);
      return enriched;
    } catch (requestError) {
      if (!options.silent) {
        alert.error(requestError.message || "Unable to load conversations.");
      }
      return [];
    } finally {
      if (!options.silent) setLoadingConversations(false);
    }
  };

  const loadHistory = async (conversation, options = {}) => {
    if (!conversation?.id) {
      setHistory(null);
      return;
    }
    if (!options.silent) setLoadingHistory(true);
    try {
      const response = await fetch(`${baseUrl}/chat-history`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ conversation_id: conversation.id }),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(
          result?.metadata?.message || "Unable to load chat history.",
        );
      }
      setHistory(result?.payload || null);
    } catch (requestError) {
      if (!options.silent) {
        alert.error(requestError.message || "Unable to load chat history.");
        setHistory(null);
      }
    } finally {
      if (!options.silent) setLoadingHistory(false);
    }
  };

  const refreshChatData = useCallback(async (options = {}) => {
    const latestConversations = await loadConversations(options);
    if (!activeConversation?.student_id) return;
    const conversation = latestConversations.find(
      (item) =>
        Number(item.student_id) === Number(activeConversation.student_id) &&
        (!teacherId || Number(item.teacher_id) === Number(teacherId)),
    );
    if (conversation) {
      const mergedConversation = mergeConversationStudent(
        conversation,
        activeChat?.conversation,
      );
      setActiveChat({ conversation: mergedConversation });
      await loadHistory(mergedConversation, options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversation?.student_id, teacherId]);

  const handleRefreshChat = async () => {
    setRefreshingChat(true);
    try {
      await refreshChatData();
    } catch (requestError) {
      alert.error(requestError.message || "Unable to refresh chat.");
    } finally {
      setRefreshingChat(false);
    }
  };

  const selectConversation = (conversation) => {
    setActiveChat({ conversation });
    setMessage("");
    setFile(null);
    loadHistory(conversation);
  };

  const resizeMessageInput = () => {
    const input = messageInputRef.current;
    if (!input) return;
    input.style.height = "auto";
    input.style.height = `${input.scrollHeight}px`;
  };

  const handleMessageChange = (event) => {
    setMessage(event.target.value);
    resizeMessageInput();
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    if (!activeConversation || sending) return;
    const trimmedMessage = message.trim();
    if (!trimmedMessage && !file) {
      alert.error("Please write a message or attach a file.");
      return;
    }
    setSending(true);
    try {
      const formData = new FormData();
      formData.append("student_id", activeConversation.student_id);
      formData.append(
        "teacher_user_id",
        activeConversation.teacher_id || teacherId,
      );
      formData.append("message", trimmedMessage);
      if (file) formData.append("file", file);

      const response = await fetch(`${baseUrl}/send-message`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.metadata?.message || "Unable to send message.");
      }
      setMessage("");
      setFile(null);
      const latestConversations = await loadConversations();
      const conversation = mergeConversationStudent(
        result?.payload?.conversation ||
          latestConversations.find(
            (item) =>
              Number(item.student_id) ===
                Number(activeConversation.student_id) &&
              (!teacherId || Number(item.teacher_id) === Number(teacherId)),
          ) ||
          activeConversation,
        activeConversation,
      );
      setActiveChat({ conversation });
      await loadHistory(conversation);
      alert.success(result?.metadata?.message || "Message sent successfully.");
    } catch (requestError) {
      alert.error(requestError.message || "Unable to send message.");
    } finally {
      setSending(false);
    }
  };

  const loadSessions = async () => {
    try {
      const response = await fetch(`${baseUrl}/session`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const result = await response.json();
      const payload = Array.isArray(result?.payload) ? result.payload : [];
      setSessions(payload);
      const active = payload.find(
        (session) => String(session.active_academic_year) === "1",
      );
      setSelectedSession(active?.id || payload[0]?.id || "");
    } catch (requestError) {
      setSessions([]);
    }
  };

  const loadClasses = async (campusId) => {
    setSelectedCampus(campusId);
    setSelectedClass("");
    setSelectedSection("");
    setSections([]);
    setStudents([]);
    if (!campusId) return;
    try {
      const response = await fetch(`${baseUrl}/campusclass/${campusId}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const result = await response.json();
      setClasses(Array.isArray(result?.payload) ? result.payload : []);
    } catch (requestError) {
      setClasses([]);
    }
  };

  const loadSections = async (
    classId,
    educationType = selectedEducationType,
  ) => {
    setSelectedClass(classId);
    setSelectedSection("");
    setStudents([]);
    if (!selectedCampus || !classId) {
      setSections([]);
      return;
    }
    try {
      const response = await fetch(
        `${baseUrl}/class_section/${selectedCampus}/${educationType}/${classId}`,
        {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        },
      );
      const result = await response.json();
      setSections(Array.isArray(result?.payload) ? result.payload : []);
    } catch (requestError) {
      setSections([]);
    }
  };

  const loadStudents = async () => {
    if (!selectedCampus) {
      alert.error("Campus is required.");
      return;
    }
    setStudentLoading(true);
    try {
      const response = await fetch(`${baseUrl}/student_filter_list`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          campus_id: selectedCampus,
          student_class_id: selectedClass,
          global_section_id: selectedSection,
          status: 2,
          education_type: selectedEducationType,
          year_id: selectedSession,
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(
          result?.metadata?.message || "Unable to load students.",
        );
      }
      const payload = Array.isArray(result?.payload) ? result.payload : [];
      setStudents(
        payload.sort((a, b) =>
          String(a.admission_id || "").localeCompare(
            String(b.admission_id || ""),
            undefined,
            { numeric: true },
          ),
        ),
      );
    } catch (requestError) {
      alert.error(requestError.message || "Unable to load students.");
      setStudents([]);
    } finally {
      setStudentLoading(false);
    }
  };

  const openStudentModal = () => {
    setStudentModal(true);
    if (!sessions.length) loadSessions();
    if (selectedCampus && !classes.length) loadClasses(selectedCampus);
  };

  const startChat = (student) => {
    const conversation = conversations.find(
      (item) =>
        Number(item.student_id) === Number(student.id) &&
        (!teacherId || Number(item.teacher_id) === Number(teacherId)),
    );
    const nextConversation = conversation || {
      id: null,
      teacher_id: teacherId,
      student_id: student.id,
      student,
    };
    setActiveChat({ conversation: nextConversation });
    setHistory(
      conversation ? null : { conversation: nextConversation, messages: [] },
    );
    setMessage("");
    setFile(null);
    setStudentModal(false);
    if (conversation) loadHistory(conversation);
  };

  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherId]);

  useAdaptiveChatPolling({
    enabled: Boolean(teacherId && hasStaffChatRole(user)),
    hasActiveConversation: Boolean(activeConversation?.id),
    onPoll: () => refreshChatData({ silent: true }),
  });

  useEffect(() => {
    if (selectedCampus) loadClasses(selectedCampus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length, loadingHistory]);

  useEffect(() => {
    resizeMessageInput();
  }, [message]);

  const activeStudent =
    activeChat?.conversation?.student || history?.conversation?.student;

  return (
    <section className="teacher-chat-page">
      <div className="teacher-chat-topbar mt-8">
        <div>
          <h1>Teacher Chat</h1>
          <p>Chat with parents for students assigned to your classes.</p>
        </div>
        <div className="teacher-chat-topbar-actions">
          <div className="teacher-chat-unread-stat">
            <span>{totalUnreadCount}</span>
            <small>Unread</small>
          </div>
          <button
            type="button"
            className="teacher-chat-start-btn btn-primary"
            onClick={openStudentModal}
          >
            <BsChatSquareText /> Start Chat
          </button>
        </div>
      </div>

      <div className="teacher-chat-shell mb-4">
        <aside className="teacher-chat-conversations">
          <label className="teacher-chat-search">
            <Search />
            <input
              type="search"
              value={conversationSearch}
              onChange={(event) => setConversationSearch(event.target.value)}
              placeholder="Search conversations"
            />
          </label>
          <div className="teacher-chat-list">
            {loadingConversations ? (
              <PageState loading title="Loading conversations" />
            ) : visibleConversations.length ? (
              visibleConversations.map((conversation) => {
                const unreadCount = Number(conversation.unread_count || 0);
                return (
                  <button
                    type="button"
                    className={`teacher-chat-conversation${
                      activeConversation?.id === conversation.id
                        ? " active"
                        : ""
                    }`}
                    key={conversation.id}
                    onClick={() => selectConversation(conversation)}
                  >
                    <span className="teacher-chat-avatar">
                      <PersonOutline color={config?.btnColor} />
                    </span>
                    <span className="teacher-chat-contact-copy">
                      <strong>{conversation.student?.name || "Student"}</strong>
                      <small>
                        {studentAcademicLine(conversationStudent(conversation))}
                      </small>
                    </span>
                    {unreadCount > 0 && (
                      <span
                        className="teacher-chat-contact-unread"
                        aria-label={`${unreadCount} unread messages`}
                      >
                        {unreadCount}
                      </span>
                    )}
                  </button>
                );
              })
            ) : (
              <div className="teacher-chat-empty">
                <ChatBubbleOutline />
                <h3>No conversations</h3>
                <p>Use Start Chat to begin a parent conversation.</p>
              </div>
            )}
          </div>
        </aside>

        <div className="teacher-chat-panel">
          {activeConversation ? (
            <>
              <header className="teacher-chat-panel-header">
                <div>
                  <h2>{activeStudent?.name || "Student"}</h2>
                  <p>{studentAcademicHeader(activeStudent)}</p>
                </div>
                <button
                  type="button"
                  className="teacher-chat-refresh-button"
                  onClick={handleRefreshChat}
                  disabled={refreshingChat || loadingHistory}
                  aria-label="Refresh chat"
                  title="Refresh chat"
                >
                  <Refresh />
                </button>
              </header>

              <div className="teacher-chat-messages">
                {loadingHistory ? (
                  <PageState loading title="Loading history" />
                ) : messages.length ? (
                  messages.map((item) => {
                    const isTeacher =
                      item.sender_type === "teacher" ||
                      Number(item.sender_id) === Number(teacherId);
                    return (
                      <article
                        className={`teacher-chat-message${
                          isTeacher ? " teacher" : " parent"
                        }`}
                        key={item.id}
                      >
                        <div>
                          <p>{item.message || "Attachment"}</p>
                          {item.file_path && (
                            <a
                              href={fileUrl(item.file_path)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <InsertDriveFileOutlined />
                              {item.file_type || "Attachment"}
                            </a>
                          )}
                          <time>{formatDateTime(item.created_at)}</time>
                        </div>
                      </article>
                    );
                  })
                ) : (
                  <div className="teacher-chat-empty">
                    <ChatBubbleOutline />
                    <h3>Start the conversation</h3>
                    <p>Send a message to this student's parent.</p>
                  </div>
                )}
                <span ref={messagesEndRef} />
              </div>

              <form className="teacher-chat-composer" onSubmit={sendMessage}>
                {file && (
                  <div className="teacher-chat-file-chip">
                    <InsertDriveFileOutlined />
                    <span>{file.name}</span>
                    <button type="button" onClick={() => setFile(null)}>
                      <Delete />
                    </button>
                  </div>
                )}
                <div className="teacher-chat-composer-row">
                  <textarea
                    ref={messageInputRef}
                    value={message}
                    onChange={handleMessageChange}
                    placeholder="Type your message"
                    rows={1}
                  />
                  <label className="teacher-chat-file-button">
                    <AttachFile />
                    <input
                      type="file"
                      onChange={(event) =>
                        setFile(event.target.files?.[0] || null)
                      }
                    />
                  </label>
                  <button
                    type="submit"
                    className="teacher-chat-send-button btn-primary"
                    disabled={sending}
                    aria-label={sending ? "Sending message" : "Send message"}
                    title={sending ? "Sending message" : "Send message"}
                  >
                    <Send />
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="teacher-chat-state">
              <ChatBubbleOutline />
              <h2>Select or start a chat</h2>
              <p>
                Pick an existing conversation or start a chat with a student
                parent.
              </p>
            </div>
          )}
        </div>
      </div>

      <StudentPickerModal
        show={studentModal}
        onHide={() => setStudentModal(false)}
        sessions={sessions}
        campuses={campuses}
        classes={classes}
        sections={sections}
        selectedSession={selectedSession}
        selectedCampus={selectedCampus}
        selectedClass={selectedClass}
        selectedSection={selectedSection}
        selectedEducationType={selectedEducationType}
        studentSearch={studentSearch}
        studentLoading={studentLoading}
        students={visibleStudents}
        onSessionChange={setSelectedSession}
        onCampusChange={loadClasses}
        onClassChange={loadSections}
        onSectionChange={setSelectedSection}
        onEducationTypeChange={(value) => {
          setSelectedEducationType(value);
          setSelectedSection("");
          setSections([]);
          setStudents([]);
          if (selectedClass) loadSections(selectedClass, value);
        }}
        onSearchChange={setStudentSearch}
        onLoadStudents={loadStudents}
        onStartChat={startChat}
      />
    </section>
  );
}

function StudentPickerModal({
  show,
  onHide,
  sessions,
  campuses,
  classes,
  sections,
  selectedSession,
  selectedCampus,
  selectedClass,
  selectedSection,
  selectedEducationType,
  studentSearch,
  studentLoading,
  students,
  onSessionChange,
  onCampusChange,
  onClassChange,
  onSectionChange,
  onEducationTypeChange,
  onSearchChange,
  onLoadStudents,
  onStartChat,
}) {
  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Start Chat</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="teacher-chat-modal-filters row">
          <div className="teacher-chat-field col-lg-3 col-md-4 col-sm-6">
            <label>Session</label>
            <select
              style={{ height: 40 }}
              className="form-select"
              value={selectedSession}
              onChange={(event) => onSessionChange(event.target.value)}
            >
              <option value="">Select Session</option>
              {sessions.map((session) => (
                <option value={session.id} key={session.id}>
                  {session.year}
                </option>
              ))}
            </select>
          </div>
          <div className="teacher-chat-field col-lg-3 col-md-4 col-sm-6">
            <label>Campus</label>
            <select
              style={{ height: 40 }}
              className="form-select"
              value={selectedCampus}
              onChange={(event) => onCampusChange(event.target.value)}
            >
              <option value="">Select Campus</option>
              {campuses.map((campus) => (
                <option value={campus.id} key={campus.id}>
                  {campus.name}
                </option>
              ))}
            </select>
          </div>
          <div className="teacher-chat-field col-lg-3 col-md-4 col-sm-6">
            <label>Class</label>
            <select
              style={{ height: 40 }}
              className="form-select"
              value={selectedClass}
              onChange={(event) => onClassChange(event.target.value)}
            >
              <option value="">Select Class</option>
              {classes.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <div className="teacher-chat-field col-lg-3 col-md-4 col-sm-6">
            <label>Section</label>
            <select
              style={{ height: 40 }}
              className="form-select"
              value={selectedSection}
              onChange={(event) => onSectionChange(event.target.value)}
            >
              <option value="">Select Section</option>
              {sections.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <div className="teacher-chat-field col-lg-3 col-md-4 col-sm-6">
            <label>Education Type</label>
            <select
              style={{ height: 40 }}
              className="form-select"
              value={selectedEducationType}
              onChange={(event) => onEducationTypeChange(event.target.value)}
            >
              <option value="1">Education</option>
              <option value="2">Hifz</option>
            </select>
          </div>
          <div className="teacher-chat-field col-lg-2 col-md-4 col-sm-6 d-flex align-items-end">
            <button
              type="button"
              className="teacher-chat-primary-btn btn-primary"
              onClick={onLoadStudents}
              disabled={studentLoading}
            >
              <Refresh /> {studentLoading ? "Loading" : "Show"}
            </button>
          </div>
        </div>

        <div className="teacher-chat-field" style={{ marginBottom: 12 }}>
          <label>Search Student</label>
          <input
            type="search"
            value={studentSearch}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search by name, admission ID or father name"
          />
        </div>

        <div className="teacher-chat-student-table-wrap">
          <table className="teacher-chat-student-table">
            <thead>
              <tr className="text-center">
                <th>Student</th>
                <th>Admission ID</th>
                <th>Father Name</th>
                <th>Class</th>
                <th>Section</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {studentLoading ? (
                <tr>
                  <td colSpan="6">
                    <PageState loading title="Loading students" />
                  </td>
                </tr>
              ) : students.length ? (
                students.map((student) => (
                  <tr key={student.id}>
                    <td>
                      <div className="teacher-chat-student">
                        {student.picture ? (
                          <img src={student.picture} alt={student.name} />
                        ) : (
                          <span className="teacher-chat-student-avatar">
                            {(student.name || "S").charAt(0)}
                          </span>
                        )}
                        <strong>{student.name}</strong>
                      </div>
                    </td>
                    <td>{student.admission_id}</td>
                    <td>{student.father_name || "-"}</td>
                    <td>{studentClassName(student) || "-"}</td>
                    <td>{studentSectionName(student) || "-"}</td>
                    <td>
                      <button
                        type="button"
                        className="teacher-chat-row-action btn-primary"
                        onClick={() => onStartChat(student)}
                        aria-label={`Start chat with ${student.name}`}
                        title="Start Chat"
                      >
                        <RiSendPlaneLine />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">
                    <div className="teacher-chat-empty">
                      <PersonOutline />
                      <h3>No students found</h3>
                      <p>
                        Choose filters and press Show to load active students.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Modal.Body>
    </Modal>
  );
}

function PageState({ title, text, loading, icon: Icon = ErrorOutline }) {
  return (
    <div className="teacher-chat-state" role="status">
      {loading ? <span className="teacher-chat-spinner" /> : <Icon />}
      <h2>{title}</h2>
      {text && <p>{text}</p>}
    </div>
  );
}
